import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Flame, Crown, Medal, Award } from "lucide-react";
import { formatJamaicaDate as formatDate } from "@/lib/datetime";

interface LeaderboardEntry {
  rank: number;
  userId: string;
  name: string;
  email: string;
  grade: number;
  weeklyXp: number;
  questionsAnswered: number;
  correctAnswers: number;
}

interface ParticipationStat {
  grade: number;
  active_students: number;
  total_xp: number;
  total_questions: number;
}

interface BadgeCount {
  badge_type: string;
  count: number;
}

const BADGE_LABELS: Record<string, { label: string; icon: typeof Trophy; color: string }> = {
  weekly_champion: { label: "Weekly Champion", icon: Crown, color: "text-yellow-500" },
  weekly_top3: { label: "Podium Finish", icon: Medal, color: "text-orange-500" },
  weekly_top10: { label: "Top 10", icon: Trophy, color: "text-blue-500" },
  champion_5x: { label: "5x Champion", icon: Crown, color: "text-amber-600" },
  champion_10x: { label: "10x Champion", icon: Crown, color: "text-red-600" },
};

export default function AdminLeaderboard() {
  const [gradeFilter, setGradeFilter] = useState("all");

  const { data, isLoading } = useQuery<{
    currentWeek: { weekStart: string; entries: LeaderboardEntry[] };
    pastWeeks: any[];
    badgeCounts: BadgeCount[];
    participation: ParticipationStat[];
  }>({
    queryKey: ["/api/admin/leaderboard", gradeFilter],
    queryFn: async () => {
      const params = gradeFilter !== "all" ? `?grade=${gradeFilter}` : "";
      const res = await fetch(`/api/admin/leaderboard${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
        <p className="mt-4 text-muted-foreground">Loading leaderboard data...</p>
      </div>
    );
  }

  const currentWeek = data?.currentWeek;
  const participation = data?.participation || [];
  const badgeCounts = data?.badgeCounts || [];
  const academyTopUsers = (data as any)?.academyTopUsers || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Leaderboard Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Weekly leaderboard data, participation stats, and badge tracking
          </p>
        </div>
        <Select value={gradeFilter} onValueChange={setGradeFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Filter Grade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Grades</SelectItem>
            <SelectItem value="4">Grade 4</SelectItem>
            <SelectItem value="5">Grade 5</SelectItem>
            <SelectItem value="6">Grade 6</SelectItem>
            <SelectItem value="7">Grade 7</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Participation Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {participation.map((stat: any) => (
          <Card key={stat.grade}>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">Grade {stat.grade}</p>
              <p className="text-2xl font-bold">{Number(stat.active_students)}</p>
              <p className="text-xs text-muted-foreground">active this week</p>
              <p className="text-sm font-medium text-primary mt-1">{Number(stat.total_xp).toLocaleString()} XP</p>
            </CardContent>
          </Card>
        ))}
        {participation.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="p-6 text-center text-muted-foreground">
              No activity this week yet
            </CardContent>
          </Card>
        )}
      </div>

      {/* Badge Distribution */}
      {badgeCounts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Award className="w-5 h-5" />
              Badge Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {badgeCounts.map((bc: any) => {
                const config = BADGE_LABELS[bc.badge_type];
                const Icon = config?.icon || Trophy;
                return (
                  <div key={bc.badge_type} className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
                    <Icon className={`w-4 h-4 ${config?.color || 'text-muted-foreground'}`} />
                    <span className="text-sm font-medium">{config?.label || bc.badge_type}</span>
                    <Badge variant="secondary">{Number(bc.count)}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Week Rankings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Flame className="w-5 h-5 text-orange-500" />
            This Week's Rankings
            {currentWeek?.weekStart && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                Week of {formatDate(currentWeek.weekStart)}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!currentWeek?.entries?.length ? (
            <p className="text-center text-muted-foreground py-6">No activity this week</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-2 w-12">Rank</th>
                    <th className="text-left py-2 px-2">Student</th>
                    <th className="text-left py-2 px-2">Email</th>
                    <th className="text-center py-2 px-2">Grade</th>
                    <th className="text-right py-2 px-2">XP</th>
                    <th className="text-right py-2 px-2">Questions</th>
                    <th className="text-right py-2 px-2">Correct</th>
                    <th className="text-right py-2 px-2">Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {currentWeek.entries.map((entry) => (
                    <tr key={entry.userId} className={`border-b hover:bg-muted/30 ${entry.rank <= 3 ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''}`}>
                      <td className="py-2 px-2 font-bold">
                        {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
                      </td>
                      <td className="py-2 px-2 font-medium">{entry.name}</td>
                      <td className="py-2 px-2 text-muted-foreground">{entry.email}</td>
                      <td className="py-2 px-2 text-center">{entry.grade}</td>
                      <td className="py-2 px-2 text-right font-bold">{entry.weeklyXp.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right">{entry.questionsAnswered}</td>
                      <td className="py-2 px-2 text-right">{entry.correctAnswers}</td>
                      <td className="py-2 px-2 text-right">
                        {entry.questionsAnswered > 0 ? Math.round((entry.correctAnswers / entry.questionsAnswered) * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Academy All-Time Rankings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Academy All-Time Top 20
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!academyTopUsers.length ? (
            <p className="text-center text-muted-foreground py-6">No data</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-2 w-12">Rank</th>
                    <th className="text-left py-2 px-2">Student</th>
                    <th className="text-left py-2 px-2">Email</th>
                    <th className="text-center py-2 px-2">Grade</th>
                    <th className="text-right py-2 px-2">Total XP</th>
                    <th className="text-right py-2 px-2">Questions</th>
                    <th className="text-right py-2 px-2">Correct</th>
                    <th className="text-right py-2 px-2">Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {academyTopUsers.map((entry: any) => (
                    <tr key={entry.rank} className={`border-b hover:bg-muted/30 ${entry.rank <= 3 ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''}`}>
                      <td className="py-2 px-2 font-bold">
                        {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
                      </td>
                      <td className="py-2 px-2 font-medium">{entry.name}</td>
                      <td className="py-2 px-2 text-muted-foreground">{entry.email}</td>
                      <td className="py-2 px-2 text-center">{entry.grade}</td>
                      <td className="py-2 px-2 text-right font-bold">{entry.totalXp.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right">{entry.totalQuestions}</td>
                      <td className="py-2 px-2 text-right">{entry.correctAnswers}</td>
                      <td className="py-2 px-2 text-right">
                        {entry.totalQuestions > 0 ? Math.round((entry.correctAnswers / entry.totalQuestions) * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
